import { useEffect, useRef } from "react";

export default function CompareSection() {
  const zoneRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const zone = zoneRef.current;
    const section = sectionRef.current;
    const cards = section.querySelectorAll(".panel");
    const fills = section.querySelectorAll(".fill");

    const isDesktop = window.matchMedia("(min-width: 901px)").matches;
    let barsActive = false;
    const tiltMax = 14;

    function startBars() {
      if (barsActive) return;
      barsActive = true;

      fills.forEach((fill) => {
        const target = parseInt(fill.dataset.percent || "0", 10);
        const label = fill.querySelector(".percent");

        fill.style.transition = "width 1.2s ease-out";
        fill.style.width = target + "%";

        if (!label) return;

        const duration = 1200;
        const start = performance.now();

        function step(now) {
          const progress = Math.min((now - start) / duration, 1);
          label.textContent = Math.round(progress * target) + "%";
          if (progress < 1) requestAnimationFrame(step);
          else label.textContent = target + "%";
        }
        requestAnimationFrame(step);
      });
    }

    function resetBars() {
      barsActive = false;
      fills.forEach((fill) => {
        fill.style.width = "0%";
        const label = fill.querySelector(".percent");
        if (label) label.textContent = "0%";
      });
    }

    function applyTilt(x, y) {
      cards.forEach((card) => {
        const r = card.getBoundingClientRect();
        const rx = ((y - (r.top + r.height / 2)) / r.height) * -tiltMax;
        const ry = ((x - (r.left + r.width / 2)) / r.width) * tiltMax;
        card.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) translateZ(15px)`;
      });
    }

    function resetTilt() {
      cards.forEach((card) => {
        card.style.transform = "rotateX(0) rotateY(0) translateZ(0)";
      });
    }

    if (isDesktop) {
      const onMouseMove = (e) => applyTilt(e.clientX, e.clientY);
      const onMouseLeave = () => resetTilt();

      zone.addEventListener("mousemove", onMouseMove);
      zone.addEventListener("mouseleave", onMouseLeave);

      section.addEventListener("mouseenter", startBars);
      section.addEventListener("mouseleave", resetBars);

      return () => {
        zone.removeEventListener("mousemove", onMouseMove);
        zone.removeEventListener("mouseleave", onMouseLeave);
        section.removeEventListener("mouseenter", startBars);
        section.removeEventListener("mouseleave", resetBars);
      };
    } else {
      const onTouchStart = (e) => {
        const t = e.touches[0];
        applyTilt(t.clientX, t.clientY);

        // little pop effect
        cards.forEach((card) => {
          card.style.transition = "transform 0.08s ease";
          card.style.transform += " scale(1.03)";
        });
      };

      const onTouchMove = (e) => {
        e.preventDefault(); // stop page scroll while interacting
        const t = e.touches[0];
        applyTilt(t.clientX, t.clientY);
      };

      const onTouchEnd = () => {
        cards.forEach((card) => {
          card.style.transition = "transform 0.25s ease";
        });
        resetTilt();
      };

      zone.addEventListener("touchstart", onTouchStart, { passive: false });
      zone.addEventListener("touchmove", onTouchMove, { passive: false });
      zone.addEventListener("touchend", onTouchEnd);

      if ("IntersectionObserver" in window) {
        let played = false;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !played) {
                played = true;
                startBars();
                observer.unobserve(section);
              }
            });
          },
          { threshold: 0.35 }
        );
        observer.observe(section);
      }

      return () => {
        zone.removeEventListener("touchmove", onTouchMove);
        zone.removeEventListener("touchend", onTouchEnd);
      };
    }
  }, []);

  return (
    <>
      <style>{`
{
  margin:0;
  padding:0;
  box-sizing:border-box;
  font-family:Arial, sans-serif;
}
.compare-wrap{
  width:100%;
  min-height:100vh;
  padding:80px 5%;
  background:
    radial-gradient(60% 40% at 15% 15%, rgba(255,61,110,.18), transparent 60%),
    radial-gradient(60% 40% at 85% 80%, rgba(255,145,77,.16), transparent 60%),
    linear-gradient(180deg,#0b0b10,#0f0f16);
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  color:#fff;
}
.compare-title{
  font-size:46px;
  font-weight:900;
  text-align:center;
}
.compare-title span{
  background:linear-gradient(90deg,#ff3b69,#ff914d);
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
}
.compare-sub{
  opacity:.8;
  margin:12px 0 50px;
  text-align:center;
}
.compare-zone{
  width:100%;
  max-width:1200px;
  display:grid;
  grid-template-columns:1fr 1fr 1fr;
  gap:28px;
  perspective:1200px;
}
.panel{
  position:relative;
  background:rgba(255,255,255,.05);
  border-radius:26px;
  padding:26px;
  box-shadow:0 30px 60px rgba(0,0,0,.45), inset 0 0 0 1px rgba(255,255,255,.06);
  transform-style:preserve-3d;
  transition:transform .12s ease, box-shadow .2s ease, background .2s ease;
  overflow:hidden;
}
.panel::before{
  content:"";
  position:absolute;
  inset:-2px;
  background:linear-gradient(120deg,transparent,rgba(255,255,255,.2),transparent);
  opacity:0;
  transition:opacity .2s ease;
}
.panel::after{
  content:"";
  position:absolute;
  inset:-150% -200%;
  background:linear-gradient(45deg,transparent 45%,rgba(255,255,255,.35) 50%,transparent 55%);
  transform:translateX(-60%);
  animation:sweep 3.5s linear infinite;
  opacity:.35;
}
@keyframes sweep{ to{transform:translateX(120%);} }

.old{ opacity:.6; }
.new{
  background:linear-gradient(140deg,#ff3b69,#ff914d);
  box-shadow:0 40px 80px rgba(255,61,110,.45);
}
.panel h3{ font-size:18px; margin-bottom:14px; }
.item{
  font-size:14px;
  display:flex;
  align-items:center;
  gap:10px;
  margin:12px 0;
}
.dot{
  width:10px;
  height:10px;
  border-radius:50%;
  background:#999;
  animation:pulse 2s infinite;
}
.new .dot{ background:#fff; }
@keyframes pulse{
  0%{transform:scale(.9);box-shadow:0 0 0 rgba(255,255,255,0);}
  50%{transform:scale(1.2);box-shadow:0 0 12px rgba(255,255,255,.8);}
  100%{transform:scale(.9);box-shadow:0 0 0 rgba(255,255,255,0);}
}
.bar{
  position:relative;
  height:8px;
  border-radius:99px;
  background:rgba(255,255,255,.12);
  overflow:hidden;
  flex:1;
}
.fill{
  position:relative;
  height:100%;
  width:0;
  border-radius:99px;
  background:#fff;
  transition:width 1.2s ease-out;
}
.compare-zone .panel:not(.old):not(.new) .fill{
  background: linear-gradient(90deg, #ff3b69, #ff914d);
}
.old .fill{ background:#777; }
.percent{
  position:absolute;
  top:-18px;
  right:0;
  font-size:11px;
  color:#fff;
  opacity:.9;
}
.panel:hover{
  box-shadow:0 45px 110px rgba(255,61,110,.7);
}
.panel:hover::before{ opacity:1; }
.panel:hover .fill{ box-shadow:0 0 15px rgba(255,80,120,.9); }

@media(max-width:900px){
  .compare-zone{ grid-template-columns:1fr; }
  .compare-title{ font-size:32px; }
}
      `}</style>

      <section className="compare-wrap" id="compare" ref={sectionRef}>
        <h2 className="compare-title">
          THE <span>REAL DIFFERENCE</span>
        </h2>
        <p className="compare-sub">
          Tap or scroll into view to see how Stado outperforms traditional methods.
        </p>

        <div className="compare-zone" ref={zoneRef}>
          {/* FEATURES */}
          <div className="panel">
            <h3>Features</h3>
            {[
              ["Accuracy", 55],
              ["Tracking", 60],
              ["Speed", 58],
              ["Security", 55],
            ].map(([label, val]) => (
              <div className="item" key={label}>
                <span className="dot"></span>
                {label}
                <div className="bar">
                  <div className="fill" data-percent={val}>
                    <span className="percent">0%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* OLD */}
          <div className="panel old">
            <h3>Traditional</h3>
            {[
              ["Manual errors", 40],
              ["No live data", 35],
              ["Slow payroll", 30],
              ["Weak controls", 32],
            ].map(([label, val]) => (
              <div className="item" key={label}>
                <span className="dot"></span>
                {label}
                <div className="bar">
                  <div className="fill" data-percent={val}>
                    <span className="percent">0%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* STADO */}
          <div className="panel new">
            <h3>Stado Payroll</h3>
            {[
              ["Automation", 95],
              ["Live GPS", 98],
              ["Instant reports", 92],
              ["Role security", 96],
            ].map(([label, val]) => (
              <div className="item" key={label}>
                <span className="dot"></span>
                {label}
                <div className="bar">
                  <div className="fill" data-percent={val}>
                    <span className="percent">0%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
